import { __awaiter, __rest } from "tslib";
import { isEqual } from 'lodash';
import React, { Component, createRef, forwardRef } from 'react';
import { OFlowContext } from '../container/context';
import { useDndDrop } from '../hooks';
import { OFlowModel } from '../model';
import { generateOFlowCanvasId, getElementsPosition } from '../utils';
export * from './flow-canvas';
export * from './interface';
export * from './element';
export const fixElementsCenter = ({ canvasData, oFlowModel, canvasDom }) => {
    const { nodes = [], groups = [] } = canvasData || {};
    const elements = [...nodes, ...groups];
    const { maxLeft, maxTop, minLeft, minTop } = getElementsPosition({ elements, oFlowModel });
    const width = maxLeft - minLeft;
    const height = maxTop - minTop;
    const graphCenterLeft = (canvasDom === null || canvasDom === void 0 ? void 0 : canvasDom.clientWidth) / 2;
    const graphCenterTop = canvasDom.clientHeight / 2;
    const diffLeft = graphCenterLeft - width / 2 - minLeft;
    const diffTop = graphCenterTop - height / 2 - minTop;
    const elementsNewPropsMap = {};
    elements === null || elements === void 0 ? void 0 : elements.map((_a) => {
        var { left, top } = _a, rest = __rest(_a, ["left", "top"]);
        elementsNewPropsMap[rest.id] = Object.assign(Object.assign({}, rest), { left: left + diffLeft, top: top + diffTop });
    });
    return Object.assign(Object.assign({}, canvasData), { nodes: nodes === null || nodes === void 0 ? void 0 : nodes.map(({ id }) => elementsNewPropsMap[id]), groups: groups === null || groups === void 0 ? void 0 : groups.map(({ id }) => elementsNewPropsMap[id]) });
};
const CanvasRenderFC = forwardRef((props, ref) => {
    const { dropRef } = useDndDrop();
    const { canvasId } = props;
    return (React.createElement("div", { id: `oflow-client--canvas-${canvasId}`, style: { height: '100%', flexGrow: 1 }, ref: dom => {
            // @ts-ignore
            ref.current = dom;
            dropRef(dom);
        } }));
});
export class CanvasComponent extends Component {
    constructor(props) {
        super(props);
        this.mouseInCanvas = false;
        this.mousePosition = { x: 0, y: 0 };
        this.canvasDomRef = createRef();
        this.drawCanvasElement = (props = {}) => __awaiter(this, void 0, void 0, function* () {
            var _a, _b;
            let newParams = Object.assign({}, props);
            const previousHooks = this.model.getCommandPreviousHooks('CANVAS_DATA_UPDATE');
            for (const hookFn of previousHooks) {
                newParams = yield hookFn({ payload: newParams, model: this.canvasModel });
            }
            const { canvasData = {}, autoFixCenter = true, autoFocusCenter = true } = newParams;
            this.canvasData = autoFixCenter
                ? fixElementsCenter({
                    canvasData: canvasData,
                    oFlowModel: this.model,
                    canvasDom: (_a = this.canvasDomRef) === null || _a === void 0 ? void 0 : _a.current,
                })
                : canvasData;
            this.canvasModel.command.execute('DREW_ELEMENTS', { payload: this.canvasData }).then(e => {
                var _a, _b;
                if (autoFocusCenter) {
                    // 兼容有面板打开的情况
                    setTimeout(() => {
                        this.focusCenterWithAnimate();
                    }, 20);
                }
                (_b = (_a = this === null || this === void 0 ? void 0 : this.canvasModel) === null || _a === void 0 ? void 0 : _a.commandHistoryModel) === null || _b === void 0 ? void 0 : _b.resetCommandHistory();
            });
            (_b = this.model.getCommandPostHooks('CANVAS_DATA_UPDATE')) === null || _b === void 0 ? void 0 : _b.forEach(hookFn => {
                hookFn({
                    canvasModel: this.canvasModel,
                    payload: { canvasData, autoFixCenter, autoFocusCenter },
                });
            });
        });
        /**
         *  画布需要绑定的事件
         */
        this.onCanvasNodeMove = param => { };
        this.onCanvasGroupMove = param => { };
        this.onCanvasEdgeCreate = () => { };
        this.onCanvasEdgeDelete = () => { };
        const { id = generateOFlowCanvasId() } = props;
        this.canvasId = id;
    }
    componentDidUpdate(previousProps) {
        if (!isEqual(this.props.canvasData, previousProps.canvasData)) {
            this.drawCanvasElement(this.props);
        }
    }
    componentDidMount() {
        var _a, _b;
        const { oFlowId } = this.context;
        const { canvasData = { nodes: [], edges: [], groups: [] }, autoFixCenter = false, autoFocusCenter = false, rankConfig, } = this.props;
        this.model = OFlowModel.getModel(oFlowId);
        this.canvasData = autoFixCenter
            ? fixElementsCenter({
                canvasData,
                oFlowModel: this.model,
                canvasDom: (_a = this.canvasDomRef) === null || _a === void 0 ? void 0 : _a.current,
            })
            : canvasData;
        this.canvasModel = this.model.buildCanvasModel({
            canvasId: this.canvasId,
            canvas: this,
            canvasData: this.canvasData,
            rankConfig,
        });
        this.eventCenter = (_b = this.canvasModel) === null || _b === void 0 ? void 0 : _b.eventCenter;
        this.onCanvasMount(this.canvasDomRef.current);
        this.bindCanvasMouseEvent();
        this.drawCanvasElement({ autoFixCenter, autoFocusCenter, canvasData });
    }
    bindCanvasMouseEvent() {
        this.bindCanvasMouseMoveEvent();
    }
    bindCanvasMouseMoveEvent() {
        var _a, _b, _c, _d;
        const mouseMove = e => {
            var _a, _b, _c, _d;
            const { clientX, clientY } = e;
            this.mouseInCanvas = true;
            this.mousePosition = { x: clientX, y: clientY };
            const elementPath = (e === null || e === void 0 ? void 0 : e.path) || ((_a = e === null || e === void 0 ? void 0 : e.composedPath) === null || _a === void 0 ? void 0 : _a.call(e)) || [];
            const oflowElemetId = (_d = (_c = (_b = Array.from(elementPath)) === null || _b === void 0 ? void 0 : _b.find((dom) => { var _a; return (_a = dom === null || dom === void 0 ? void 0 : dom.attributes) === null || _a === void 0 ? void 0 : _a['oflow-element-id']; })) === null || _c === void 0 ? void 0 : _c.attributes['oflow-element-id']) === null || _d === void 0 ? void 0 : _d.value;
            if (oflowElemetId) {
                this.canvasModel.setHoveredElementId(oflowElemetId);
            }
            else {
                this.canvasModel.setHoveredElementId();
            }
        };
        const mouseLeave = e => {
            this.mouseInCanvas = false;
        };
        (_b = (_a = this.canvasDomRef) === null || _a === void 0 ? void 0 : _a.current) === null || _b === void 0 ? void 0 : _b.addEventListener('mousemove', mouseMove);
        (_d = (_c = this.canvasDomRef) === null || _c === void 0 ? void 0 : _c.current) === null || _d === void 0 ? void 0 : _d.addEventListener('mouseleave', mouseLeave);
    }
    get nodeTypeMap() {
        return this.model.nodeTypeMap;
    }
    get edgeTypeMap() {
        return this.model.edgeTypeMap;
    }
    get groupTypeMap() {
        return this.model.groupTypeMap;
    }
    render() {
        return React.createElement(CanvasRenderFC, { canvasId: this.canvasId, ref: this.canvasDomRef });
    }
}
CanvasComponent.componentName = 'CanvasComponent';
CanvasComponent.contextType = OFlowContext;
//# sourceMappingURL=index.js.map